import { sidecarWsUrl } from "./sidecar";

type Listener = (message: Record<string, unknown>) => void;

/** Mirrors `apps/desktop`'s own `SidecarWsClient` — same connection lifecycle (§6.2 of `docs/implementation/03-Local-Server-Transport-API.md`), duplicated for the same reason `sidecar-http-client.ts` is. */
export class SidecarWsClient {
  private socket: WebSocket | null = null;
  private readonly listeners = new Map<string, Set<Listener>>();
  private reconnectDelayMs = 1000;
  private reconnectTimer: ReturnType<typeof setTimeout> | null = null;
  private stopped = false;

  constructor(
    private readonly getToken: () => string | null,
    private readonly channels: readonly string[],
    /** Set only by a Browser Display's own connection (`routes/display.$outputId.tsx`) — re-sent as an `identify-output` message on every single connect, so `ConnectedDeviceRegistry` learns which output this is for even when reconnecting with a token minted before that claim existed (see `ws-message-schema.ts`'s own doc comment on why this can't just rely on the token alone). */
    private readonly outputId?: string,
  ) {}

  start(): void {
    this.stopped = false;
    this.connect();
  }

  stop(): void {
    this.stopped = true;
    if (this.reconnectTimer) clearTimeout(this.reconnectTimer);
    this.socket?.close(1000, "client stopping");
    this.socket = null;
  }

  on(type: string, listener: Listener): () => void {
    let set = this.listeners.get(type);
    if (!set) {
      set = new Set();
      this.listeners.set(type, set);
    }
    set.add(listener);
    return () => set?.delete(listener);
  }

  private connect(): void {
    const token = this.getToken();
    if (!token) {
      this.scheduleReconnect();
      return;
    }

    const socket = new WebSocket(sidecarWsUrl());
    this.socket = socket;

    socket.addEventListener("open", () => {
      socket.send(JSON.stringify({ type: "auth", token }));
    });

    socket.addEventListener("message", (event: MessageEvent<string>) => {
      let message: Record<string, unknown>;
      try {
        message = JSON.parse(event.data) as Record<string, unknown>;
      } catch {
        return;
      }

      if (message.type === "auth:ok") {
        this.reconnectDelayMs = 1000;
        socket.send(JSON.stringify({ type: "subscribe", channels: this.channels }));
        if (this.outputId) {
          socket.send(JSON.stringify({ type: "identify-output", outputId: this.outputId }));
        }
        return;
      }
      if (message.type === "auth:error") {
        socket.close(1008, "auth rejected");
        return;
      }

      const type = typeof message.type === "string" ? message.type : undefined;
      if (!type) return;
      for (const listener of this.listeners.get(type) ?? []) listener(message);
    });

    socket.addEventListener("close", () => {
      if (this.socket === socket) this.socket = null;
      this.scheduleReconnect();
    });

    socket.addEventListener("error", () => socket.close());
  }

  private scheduleReconnect(): void {
    if (this.stopped || this.reconnectTimer) return;
    this.reconnectTimer = setTimeout(() => {
      this.reconnectTimer = null;
      if (!this.stopped) this.connect();
    }, this.reconnectDelayMs);
    this.reconnectDelayMs = Math.min(this.reconnectDelayMs * 2, 15_000);
  }
}
